import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import InViewReveal from "@/components/ledger/InViewReveal";
import SectionWatermark from "@/components/ledger/SectionWatermark";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";

/**
 * Our Story - long-form editorial essay across four chapters.
 *
 * Narrative arc (chronological, named, specific - these three together are
 * the documented psychology levers for founder-led trust):
 *   I    Counter    Dariyalal Sales Agency  - a retail shop in Gujarat
 *   II   Warehouse  Wholesale supply        - from one shop to many buyers
 *   III  Mill       Vilora Organic Essential Pvt. Ltd. - manufacturing,
 *                                              dry-fruit processing
 *   IV   Port       Vilora Impex            - international export, today
 *
 * Closes with a founders' signed-letter section linking to the two
 * personal sites, then a single CTA back to the inquiry desk.
 */

const CHAPTERS = [
  {
    id: "counter",
    phase: "I",
    year: "Origin",
    eyebrow: "The Counter",
    title: "Dariyalal Sales Agency",
    standfirst:
      "A retail shop in Rajkot. The first counter, the first ledger, the first relationships that taught us how trust is actually weighed and handed over.",
    body: [
      "Before there were containers, there was a counter. Bags of grain stacked behind, customers reading prices off a hand-written card, the scale tipping back to balance after every sale. Dariyalal Sales Agency was - and still is - a small retail shop where a family learned what a careful gram looks like, what a fair price feels like, and why the same buyer comes back next month.",
      "Everything we do at scale today, we first did across that counter. Grade by sight. Weigh twice. Wrap with the buyer's name said out loud. Trust, when you watch it built one transaction at a time, stops being a slogan.",
    ],
    pullQuote:
      "We didn't move into export from a boardroom. We moved into it from a shop counter where the customer's eye was on the scale.",
    image: "/story/dsa-shop-1.jpg",
    imageAlt:
      "Dariyalal Sales Agency - interior of the original retail shop",
    href: "https://www.dariyalalsalesagency.shop",
    hrefLabel: "dariyalalsalesagency.shop",
    hrefBadge: "Still open",
  },
  {
    id: "warehouse",
    phase: "II",
    year: "Growth",
    eyebrow: "The Warehouse",
    title: "Into Wholesale Supply",
    standfirst:
      "When the counter wasn't enough, the warehouse opened. One shop became a network of buyers, retailers, kitchens, and small institutions across Gujarat.",
    body: [
      "The shift from retail to wholesale was the first real test of whether the discipline that worked across a counter would survive a truck loading bay. It did - but only because nothing of the original was left behind. The ledger got bigger. The scale stayed.",
      "Wholesale taught us logistics: cold-chain windows, lot traceability, packaging integrity, the politics of shared docks. It also taught us patience - the calm of preparing a shipment well before the buyer asked for it.",
    ],
    pullQuote:
      "Scale didn't change the principle - it only made it more expensive to skip.",
    image: "/story/dsa-shop-2.jpg",
    imageAlt:
      "Wholesale stage - Dariyalal Sales Agency stockroom and bulk goods",
  },
  {
    id: "mill",
    phase: "III",
    year: "Manufacturing",
    eyebrow: "The Mill",
    title: "Vilora Organic Essential Pvt. Ltd.",
    standfirst:
      "We stopped being only middlemen. We started owning the process - sourcing, cleaning, grading, and packing dry fruits and naturals under our own roof.",
    body: [
      "Vilora Organic Essential was incorporated to bring the production line under the same ledger discipline as the shop. Manufacturing meant we could finally answer the question every serious buyer asks: who graded this lot, on what date, against which standard? The answer is now a name and a signature, not a vague third-party.",
      "The Vilora mill cleans and grades dry fruits and naturals to export-ready specification - sortex-cleaned, sized, residue-tested, and packed for the long haul.",
    ],
    pullQuote:
      "Owning the line meant owning the answer - every lot, every shipment, every signature.",
    href: "https://www.viloranaturals.com",
    hrefLabel: "viloranaturals.com",
    hrefBadge: "Active",
    isOrganicSeal: true,
  },
  {
    id: "port",
    phase: "IV",
    year: "Today",
    eyebrow: "The Port",
    title: "Vilora Impex",
    standfirst:
      "An Indian trading house writing the next chapter - premium grain, spice, dry goods, and fresh produce moved through Mundra and Kandla to ports across the Gulf, Africa, and Asia Pacific.",
    body: [
      "Vilora Impex is the export desk built on top of fifteen years of counter-and-warehouse discipline. We move the same goods we always have - grown by the same farmers, cleaned in the same Vilora mill, weighed on a scale we still trust - but now to buyers in Doha, Jebel Ali, Dar es Salaam, Singapore, and beyond.",
      "Every shipment carries the same things the shop carried: a graded lot, a clean paper trail, two photographs (warehouse, port), and a name on the bill of lading. The ledger is bigger. The discipline is the same.",
    ],
    pullQuote:
      "From a counter in Rajkot to a container at Mundra - the route is long, the principle is unchanged.",
    isCurrent: true,
    isLogoCard: true,
  },
];

const FOUNDERS = [
  {
    name: "Darshit Anadkat",
    role: "Founder",
    initial: "D",
    bio:
      "Carries the original ledger. Spent more years behind the shop counter than behind a desk; runs origin sourcing, grading, and the disciplines that came from there.",
    href: "https://www.darshitanadkat.com",
    hrefLabel: "darshitanadkat.com",
  },
  {
    name: "Hevin Dudhatra",
    role: "Co-founder",
    initial: "H",
    bio:
      "Carries the export desk. Manufacturing, processing, paperwork, ports - the path the goods take after the lot is graded; the side of the house that talks to the buyer at 4 a.m. local time.",
    href: "https://www.hevindudhatra.com",
    hrefLabel: "hevindudhatra.com",
  },
];

export default function OurStoryPage() {
  useSeo({
    title: "Our Story | Vilora Impex — From Dariyalal Sales Agency to International Export",
    description:
      "The four-chapter history of Vilora Impex: from a Gujarat retail counter to wholesale supply, the Vilora Organic Essential mill, and today's international export desk.",
    path: "/our-story",
    image: "https://www.viloraimpex.com/og.jpg",
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://www.viloraimpex.com/our-story#article",
          "headline": "From Dariyalal Sales Agency to Vilora Impex — Four Chapters of an Indian Trading House",
          "alternativeHeadline": "The Vilora Story",
          "description":
            "Four chapters of Vilora Impex: a retail counter in Gujarat, wholesale supply, the Vilora Organic Essential mill, and today's international export desk.",
          "image": "https://www.viloraimpex.com/og.jpg",
          "datePublished": "2025-09-01",
          "dateModified": "2026-05-16",
          "author": [
            {
              "@type": "Person",
              "@id": "https://www.viloraimpex.com/our-story#anadkat",
              "name": "D. Anadkat",
              "jobTitle": "Founder",
              "worksFor": { "@id": "https://www.viloraimpex.com/#organization" }
            },
            {
              "@type": "Person",
              "@id": "https://www.viloraimpex.com/our-story#dudhatra",
              "name": "H. Dudhatra",
              "jobTitle": "Co-founder",
              "worksFor": { "@id": "https://www.viloraimpex.com/#organization" }
            }
          ],
          "publisher": { "@id": "https://www.viloraimpex.com/#organization" },
          "isPartOf": { "@id": "https://www.viloraimpex.com/#website" },
          "mainEntityOfPage": "https://www.viloraimpex.com/our-story",
          "inLanguage": "en",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".story-hero-title", ".story-hero-deck", ".story-chapter-standfirst"]
          },
          "about": [
            { "@type": "Thing", "name": "Indian trading house" },
            { "@type": "Thing", "name": "Rice export from Gujarat" },
            { "@type": "Thing", "name": "Spice export from India" },
            { "@type": "Thing", "name": "Vilora Organic Essential Pvt. Ltd." },
            { "@type": "Thing", "name": "Dariyalal Sales Agency" }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.viloraimpex.com/" },
            { "@type": "ListItem", "position": 2, "name": "Our Story", "item": "https://www.viloraimpex.com/our-story" }
          ]
        }
      ]
    },
  });

  return (
    <main
      className="ledger story-page relative min-h-screen"
      data-testid="our-story-page"
    >
      <ScrollProgress />
      <Navbar />
      <StoryHero />
      <ChapterNav />
      <StoryChapters />
      <FoundersBlock />
      <StoryClose />
      <FloatingWhatsApp />
    </main>
  );
}

/* ─── HERO ─────────────────────────────────────────────────────────── */

function StoryHero() {
  return (
    <section
      id="story-top"
      className="story-hero relative paper"
      style={{ overflow: "hidden" }}
    >
      <SectionWatermark variant="compass" position="top-right" size={460} opacity={0.12} />

      <div className="story-hero-shell">
        {/* Folio rail */}
        <div className="story-folio">
          <Link to="/" className="story-back-link">
            <ArrowLeft size={14} strokeWidth={1.6} />
            <span>Back to Home</span>
          </Link>
          <span className="rule rule-grow" />
          <span className="folio">VOL · I - OUR STORY</span>
        </div>

        {/* Eyebrow */}
        <div className="story-hero-eyebrow">
          <span className="brass-ribbon">A House in Four Chapters</span>
          <span className="meta">RAJKOT · GUJARAT · INDIA</span>
        </div>

        {/* The big spread */}
        <div className="story-hero-spread">
          <h1 className="story-hero-title display">
            From a <em className="display-italic vermilion">counter</em>
            <br />
            in Rajkot,
            <br />
            to a <em className="display-italic vermilion">container</em>
            <br />
            at Mundra.
          </h1>

          <div className="story-hero-deck-col">
            <p className="story-hero-deck">
              This is not a corporate origin story. It begins in a small shop
              with a hand-written ledger, moves through a wholesale floor, then
              a mill, and now a sealed forty-foot container leaving Mundra at
              dawn. <em>Same family, same scale, same discipline - bigger room.</em>
            </p>

            <ol className="story-hero-arc">
              {CHAPTERS.map((c) => (
                <li key={c.id}>
                  <span className="story-arc-phase">{c.phase}</span>
                  <span className="story-arc-eyebrow">{c.eyebrow}</span>
                  <span className="story-arc-year">{c.year}</span>
                </li>
              ))}
            </ol>

            <div className="story-hero-signed">
              <span className="story-hero-signed-line">- Signed,</span>
              <span className="story-hero-signed-names">
                D. Anadkat &amp; H. Dudhatra
              </span>
              <span className="story-hero-signed-place">
                Rajkot, Gujarat &middot; 2025
              </span>
            </div>
          </div>
        </div>

        <ScrollCue />
      </div>
    </section>
  );
}

function ScrollCue() {
  return (
    <a href="#chapter-counter" className="story-scroll-cue" aria-label="Begin">
      <span className="mono">BEGIN - CHAPTER I</span>
      <span className="story-scroll-cue-rule" aria-hidden="true" />
      <span className="story-scroll-cue-arrow" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M7 1v12M2 8l5 5 5-5"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="square"
          />
        </svg>
      </span>
    </a>
  );
}

/* ─── CHAPTER NAVIGATOR ────────────────────────────────────────────── */

function ChapterNav() {
  const [active, setActive] = useState("counter");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id.replace("chapter-", ""));
        });
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    CHAPTERS.forEach((c) => {
      const el = document.getElementById(`chapter-${c.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <nav className="story-chapter-nav" aria-label="Story chapters">
      <div className="story-chapter-nav-inner">
        {CHAPTERS.map((c) => (
          <a
            key={c.id}
            href={`#chapter-${c.id}`}
            className={`story-chapter-nav-item ${active === c.id ? "is-active" : ""}`}
          >
            <span className="story-chapter-nav-phase">{c.phase}</span>
            <span className="story-chapter-nav-label">{c.eyebrow}</span>
            <span className="story-chapter-nav-year">{c.year}</span>
          </a>
        ))}
      </div>
    </nav>
  );
}

/* ─── CHAPTERS ─────────────────────────────────────────────────────── */

function StoryChapters() {
  return (
    <div className="story-chapters" role="list">
      {CHAPTERS.map((c, i) => (
        <Chapter key={c.id} chapter={c} reversed={i % 2 === 1} />
      ))}
    </div>
  );
}

function Chapter({ chapter, reversed }) {
  const ref = useRef(null);

  // Subtle parallax on chapter media - translateY follows scroll position
  // within the section. Pure transform, GPU-friendly.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const media = el.querySelector(".story-chapter-media-inner");
    if (!media) return;
    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const offset = (clamped - 0.5) * 28;
      media.style.transform = `translate3d(0, ${offset}px, 0)`;
      raf = 0;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <article
      id={`chapter-${chapter.id}`}
      ref={ref}
      role="listitem"
      className={`story-chapter ${reversed ? "is-reversed" : ""} ${
        chapter.isCurrent ? "is-current" : ""
      }`}
    >
      <div className="story-chapter-grid">
        {/* Media side */}
        <div className="story-chapter-media">
          <div className="story-chapter-phase">
            <span className="story-chapter-phase-pre">PHASE</span>
            <span className="story-chapter-phase-num display-italic">{chapter.phase}</span>
            <span className="story-chapter-phase-year">{chapter.year}</span>
          </div>

          <div className="story-chapter-media-frame">
            <div className="story-chapter-media-inner">
              {chapter.isOrganicSeal ? (
                <OrganicSealCard />
              ) : chapter.isLogoCard ? (
                <LogoCard />
              ) : (
                <img
                  src={chapter.image}
                  alt={chapter.imageAlt}
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="story-chapter-media-shade" aria-hidden="true" />
            </div>
            <div className="story-chapter-media-corner story-chapter-media-corner--tl" aria-hidden="true" />
            <div className="story-chapter-media-corner story-chapter-media-corner--br" aria-hidden="true" />
          </div>

          {chapter.image && !chapter.isStampCard && !chapter.isLogoCard ? (
            <div className="story-chapter-plate-label mono">
              PLATE · {chapter.phase}
            </div>
          ) : null}
        </div>

        {/* Copy side */}
        <div className="story-chapter-copy">
          <InViewReveal>
            <div className="story-chapter-eyebrow">
              <span className="folio">{chapter.eyebrow}</span>
              <span className="rule" style={{ width: 36, height: 1 }} />
              <span className="meta">CHAPTER {chapter.phase}</span>
            </div>
            <h2 className="story-chapter-title display">{chapter.title}</h2>
            <p className="story-chapter-standfirst">{chapter.standfirst}</p>
          </InViewReveal>

          <InViewReveal delay={2}>
            {chapter.body.map((p, idx) => (
              <p
                key={idx}
                className={`story-chapter-paragraph ${idx === 0 ? "has-dropcap" : ""}`}
              >
                {p}
              </p>
            ))}
          </InViewReveal>

          {chapter.pullQuote ? (
            <InViewReveal delay={3}>
              <blockquote className="story-chapter-quote">
                <span className="story-chapter-quote-mark" aria-hidden="true">
                  &ldquo;
                </span>
                <span className="story-chapter-quote-text display-italic">
                  {chapter.pullQuote}
                </span>
              </blockquote>
            </InViewReveal>
          ) : null}

          {chapter.href ? (
            <InViewReveal delay={3}>
              <a
                href={chapter.href}
                target="_blank"
                rel="noopener noreferrer"
                className="story-chapter-visit"
              >
                <span className="story-chapter-visit-label">Visit</span>
                <span className="story-chapter-visit-host">{chapter.hrefLabel}</span>
                {chapter.hrefBadge ? (
                  <span className="story-chapter-visit-badge">{chapter.hrefBadge}</span>
                ) : null}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </InViewReveal>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/* ─── SPECIAL CARDS ────────────────────────────────────────────────── */

/** OrganicSealCard - chapter III. Wax-seal style green/gold round emblem. */
function OrganicSealCard() {
  return (
    <div className="story-organic-seal-card">
      <span className="story-organic-seal-folio mono">SEAL · ORGANIC</span>
      <img
        src="/story/vilora-organic-seal.png"
        alt="Vilora Organic Essential Pvt. Ltd. - official seal"
        loading="lazy"
        decoding="async"
        className="story-organic-seal-mark"
      />
      <div className="story-organic-seal-stack">
        <span className="display-italic story-organic-seal-name">
          Vilora Organic Essential
        </span>
        <span className="meta">
          DRY FRUIT MILL · GUJARAT
        </span>
      </div>
    </div>
  );
}

/** LogoCard - chapter IV (Vilora Impex). Reused from the previous chapter
 *  III treatment, now bound to the export desk identity. */
function LogoCard() {
  return (
    <div className="story-logo-card">
      <img
        src="/logo.png"
        alt="Vilora Impex - house mark"
        loading="lazy"
        decoding="async"
        className="story-logo-card-mark"
      />
      <div className="story-logo-card-stack">
        <span className="folio">EST · 2025</span>
        <span className="display-italic story-logo-card-name">
          Vilora Impex
        </span>
        <span className="meta">
          GRAIN · SPICE · DRY GOODS · PRODUCE
        </span>
      </div>
    </div>
  );
}

/* ─── FOUNDERS ─────────────────────────────────────────────────────── */

function FoundersBlock() {
  return (
    <section
      id="founders"
      className="story-founders relative paper"
      style={{ overflow: "hidden" }}
    >
      <SectionWatermark variant="monogram" position="top-left" size={420} opacity={0.14} />

      <div className="story-founders-inner">
        <InViewReveal className="story-founders-head">
          <div className="story-folio">
            <span className="folio">№ 05</span>
            <span className="rule rule-grow" />
            <span className="folio">THE TWO WHO CARRY IT</span>
          </div>
          <h2 className="display story-founders-title">
            The ledger is shared between
            <br />
            <em className="display-italic vermilion">two pens.</em>
          </h2>
          <p className="story-founders-deck">
            Every file at Vilora Impex is opened by one of two people, then
            initialled by the other before it sails. Below - the two who carry
            it, and the work each of them carries.
          </p>
        </InViewReveal>

        <div className="story-founders-grid">
          {FOUNDERS.map((f, i) => (
            <InViewReveal
              as="article"
              className="story-founder"
              delay={i + 1}
              key={f.name}
            >
              <div className="story-founder-stamp" aria-hidden="true">
                {f.initial}
              </div>
              <div className="story-founder-meta">
                <span className="folio">{f.role.toUpperCase()}</span>
              </div>
              <h3 className="story-founder-name display">{f.name}</h3>
              <div className="story-founder-rule" aria-hidden="true" />
              <p className="story-founder-bio">{f.bio}</p>
              <a
                href={f.href}
                target="_blank"
                rel="noopener noreferrer"
                className="story-founder-link"
              >
                <span>{f.hrefLabel}</span>
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </InViewReveal>
          ))}
        </div>

        <InViewReveal className="story-founders-footnote">
          <span className="display-italic">- Signed, in Gujarat, 2025 -</span>
        </InViewReveal>
      </div>
    </section>
  );
}

/* ─── CLOSING CTA ──────────────────────────────────────────────────── */

function StoryClose() {
  return (
    <section className="story-close paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="compass" position="center" size={520} opacity={0.06} />

      <InViewReveal className="story-close-inner">
        <div className="story-folio">
          <span className="folio">№ 06</span>
          <span className="rule rule-grow" />
          <span className="folio">NEXT CHAPTER</span>
        </div>
        <h2 className="story-close-title display">
          Want to be the
          <br />
          <em className="display-italic vermilion">next chapter?</em>
        </h2>
        <p className="story-close-deck">
          Tell us the commodity, the destination port, and the date you would
          like the cargo to arrive. We will write back the same day with a price,
          a sample plan, and the documents we will need from you.
        </p>
        <div className="story-close-actions">
          <Link to="/#inquiry" className="btn-primary">
            <span className="btn-code">№ 06</span>
            <span>Open a Conversation</span>
            <span className="btn-arrow">
              <ArrowRight size={14} strokeWidth={1.6} />
            </span>
          </Link>
          <Link to="/" className="btn-ghost">
            <span>Back to Home</span>
            <span className="btn-arrow">→</span>
          </Link>
        </div>
      </InViewReveal>
    </section>
  );
}
